import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Sparkles, Send, Paperclip, X, Wand2, AlertTriangle, CheckCircle2, Plug, Copy } from "lucide-react";
import { rawFetch, rawMultipart, rawRootFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, StatusPill } from "@/components/common";
import { PaginatedTable } from "@/components/PaginatedTable";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";
import { useChannel } from "@/lib/ws";

interface AgentDef {
  id: string; name: string; model: string; enabled: boolean;
  // Phase 13.4 / G14 — true when the agent declares a response JSON-Schema,
  // so runs are parsed into a structured object ("P" = structured/parsed).
  has_structured_output?: boolean;
}
// Phase 13.7 / G17 — Celery worker-health probe (root-level /health/worker).
interface WorkerHealth {
  status: "ok" | "degraded";
  dispatch: "celery" | "inline";
  broker: { reachable: boolean; url?: string; error?: string | null };
  workers: { online: boolean; count: number; names: string[]; error?: string | null };
}
// Phase 13.4 / G4 — provider-credential posture for a single agent.
interface CredentialStatus {
  mode: "stub" | "live" | "no_key";
  provider?: string;
  model?: string;
  detail?: string;
}
interface AgentRun {
  id: string; agent: string; status: string; created_at: string;
  finished_at?: string | null; output?: unknown;
}
interface AgentTemplate {
  id: string; display_name: string; industry: string;
  description: string; default_model: string;
  suggested_ontology_industry?: string | null;
  tags?: string[];
  installed_count?: number;
}
interface ToolSpec { key: string; why?: string }
interface SubAgentSpec {
  name: string; role: string; system_prompt: string;
  tools: ToolSpec[]; model: string; needs_retrieval: boolean;
}
interface AgentBlueprint {
  topology: "single" | "supervisor";
  summary: string; rationale: string; confidence: number;
  is_directional?: boolean; caveat?: string;
  supervisor?: SubAgentSpec | null;
  agents: SubAgentSpec[];
}
interface BuildSession {
  id: string; name: string; need: string; status: string;
  blueprint?: AgentBlueprint | null;
  created_agent_ids?: string[];
}

export function Agents() {
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();
  const focusedAgentId = new URLSearchParams(window.location.search).get("agent") || "";
  const [selected, setSelected] = useState<string | null>(null);
  const [industryFilter, setIndustryFilter] = useState("");
  // Chat composer state
  const [chatAgentId, setChatAgentId] = useState<string>("");
  const [message, setMessage] = useState("Summarise what you can do for me in this workspace.");
  const [files, setFiles] = useState<File[]>([]);
  const [runtimeMode, setRuntimeMode] = useState<"default" | "supervisor">("default");
  const [maxIterations, setMaxIterations] = useState(6);
  const [threadId, setThreadId] = useState("");
  const [chatError, setChatError] = useState<string | null>(null);
  // Structured pre-flight error envelope: surfaced by the BE before a run
  // is even created when the agent is missing a model or provider key.
  const [preflight, setPreflight] = useState<{
    code: string;
    error: string;
    hint: string;
    link: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ----- "Build your agent" (natural-language architect) -----
  const [buildNeed, setBuildNeed] = useState("");
  const [buildName, setBuildName] = useState("");
  const [buildSession, setBuildSession] = useState<BuildSession | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [buildQuota, setBuildQuota] = useState<{ limit: number; current: number; hint: string } | null>(null);

  // ----- "Connect from your app" (API integration helper) -----
  const workspaces = useApp((s) => s.workspaces);
  const tenantSlug = workspaces.find((w) => w.id === ws)?.organization_slug || "<your-slug>";
  const [showConnect, setShowConnect] = useState(false);
  const [connectCopied, setConnectCopied] = useState(false);

  const probe = useQuery<{ count: number }>({
    queryKey: ["agents-probe", ws],
    queryFn: () => rawFetch(`/agents/agents/?page_size=1${ws ? `&workspace=${ws}` : ""}`),
  });

  const templates = useQuery<{ templates: AgentTemplate[] }>({
    queryKey: ["agent-templates", industryFilter, ws],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (industryFilter) qs.set("industry", industryFilter);
      if (ws) qs.set("workspace", String(ws));
      const s = qs.toString();
      return rawFetch(`/agents/templates/${s ? `?${s}` : ""}`);
    },
  });

  // Quick-trigger from the Definitions table (uses sensible defaults).
  const trigger = useMutation({
    mutationFn: (agentId: string) => rawFetch(`/agents/agents/${agentId}/trigger/`, {
      method: "POST",
      body: JSON.stringify({ message: "Hello", max_iterations: 3 }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["table", "/agents/runs/"] }),
  });

  // Activation is gated by an eval-regression check: the BE returns 409 with
  // a human-readable detail when activating would regress the golden set.
  const [gateError, setGateError] = useState<string | null>(null);
  const activate = useMutation({
    mutationFn: (agentId: string) => rawFetch(`/agents/agents/${agentId}/activate/`, { method: "POST" }),
    onSuccess: () => {
      setGateError(null);
      qc.invalidateQueries({ queryKey: ["table", "/agents/agents/"] });
      qc.invalidateQueries({ queryKey: ["agents-list", ws] });
    },
    onError: (e: Error) => setGateError(e.message),
  });
  const deactivate = useMutation({
    mutationFn: (agentId: string) => rawFetch(`/agents/agents/${agentId}/deactivate/`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/agents/agents/"] });
      qc.invalidateQueries({ queryKey: ["agents-list", ws] });
    },
  });

  // Lightweight list of definitions for the chat composer's agent picker.
  const defs = useQuery<{ results: AgentDef[] }>({
    queryKey: ["agents-list", ws],
    queryFn: () => rawFetch(`/agents/agents/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
  });

  // Phase 13.7 / G17 — poll the Celery worker-health probe so we can warn the
  // operator when runs will silently queue (no worker online) or fall back to
  // synchronous inline dispatch. Lives at the root /health/worker, outside the
  // versioned API surface, so it uses rawRootFetch. Always returns HTTP 200.
  const workerHealth = useQuery<WorkerHealth>({
    queryKey: ["worker-health"],
    queryFn: () => rawRootFetch<WorkerHealth>("/health/worker"),
    refetchInterval: 30000,
    staleTime: 15000,
    retry: false,
  });
  const wh = workerHealth.data;
  const workerDown = wh ? wh.status !== "ok" : false;

  // Chat: multipart POST to /agents/agents/<id>/chat/. Uses rawMultipart
  // which (1) lets the browser set the multipart boundary, (2) reads the
  // same BASE as the rest of the typed client (VITE_API_BASE_URL), and
  // (3) raises a clean error if a misconfigured proxy returns HTML
  // instead of JSON.
  const chat = useMutation({
    mutationFn: async (): Promise<{ id: string }> => {
      if (!chatAgentId) throw new Error("Pick an agent first.");
      if (!message.trim() && files.length === 0) {
        throw new Error("Type a message or attach a file.");
      }
      const form = new FormData();
      if (message.trim()) form.append("message", message.trim());
      form.append("runtime", runtimeMode);
      form.append("max_iterations", String(maxIterations));
      if (threadId.trim()) form.append("thread_id", threadId.trim());
      for (const f of files) form.append("files", f);
      return rawMultipart<{ id: string }>(`/agents/agents/${chatAgentId}/chat/`, form);
    },
    onSuccess: (run) => {
      setChatError(null);
      setPreflight(null);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSelected(run.id);                  // auto-subscribe to live trace
      if (!threadId) setThreadId(run.id);   // first message becomes the thread anchor
      qc.invalidateQueries({ queryKey: ["table", "/agents/runs/"] });
    },
    onError: (e: Error) => {
      // Pre-flight failures (HTTP 400) come back as a structured envelope
      // {code, error, hint, link} -- render them as an actionable CTA
      // instead of dumping a raw exception string.
      const body = (e as Error & { body?: Record<string, unknown> }).body;
      const code = body && typeof body.code === "string" ? body.code : "";
      if (code) {
        setPreflight({
          code,
          error: String(body?.error ?? body?.detail ?? e.message),
          hint: typeof body?.hint === "string" ? body.hint : "",
          link: typeof body?.link === "string" ? body.link : "",
        });
        setChatError(null);
      } else {
        setPreflight(null);
        setChatError(e.message);
      }
    },
  });

  const createFromTemplate = useMutation({
    mutationFn: (templateId: string) => rawFetch(`/agents/from-template/`, {
      method: "POST",
      body: JSON.stringify({
        workspace: ws,
        template_id: templateId,
        bind_ontology: true,
        bind_retrieval_profile: true,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents-probe", ws] });
      qc.invalidateQueries({ queryKey: ["agent-templates"] });
      qc.invalidateQueries({ queryKey: ["agents-list", ws] });
      qc.invalidateQueries({ queryKey: ["table", "/agents/agents/"] });
    },
  });

  // ----- "Build your agent" (natural-language architect) -----
  const buildScope = useMutation({
    mutationFn: async (): Promise<BuildSession> => {
      if (!buildNeed.trim()) throw new Error("Describe what you need the agent to do.");
      const session = await rawFetch<BuildSession>(`/agents/build-sessions/`, {
        method: "POST",
        body: JSON.stringify({
          workspace: ws,
          name: buildName.trim(),
          need: buildNeed.trim(),
        }),
      });
      return rawFetch<BuildSession>(`/agents/build-sessions/${session.id}/scope/`, {
        method: "POST",
        body: JSON.stringify({ need: buildNeed.trim() }),
      });
    },
    onSuccess: (session) => {
      setBuildError(null);
      setBuildQuota(null);
      setBuildSession(session);
    },
    onError: (e: Error) => {
      const body = (e as Error & { body?: Record<string, unknown> }).body;
      if (body && body.code === "agent_builds_quota_exceeded") {
        setBuildQuota({
          limit: Number(body.limit ?? 0),
          current: Number(body.current ?? 0),
          hint: typeof body.hint === "string" ? body.hint : "",
        });
        setBuildError(null);
      } else {
        setBuildQuota(null);
        setBuildError(e.message);
      }
    },
  });

  const buildGenerate = useMutation({
    mutationFn: async (): Promise<BuildSession> => {
      if (!buildSession) throw new Error("Scope an agent first.");
      return rawFetch<BuildSession>(`/agents/build-sessions/${buildSession.id}/generate/`, {
        method: "POST",
        body: JSON.stringify({ blueprint: buildSession.blueprint }),
      });
    },
    onSuccess: (session) => {
      setBuildSession(session);
      setBuildError(null);
      qc.invalidateQueries({ queryKey: ["agents-probe", ws] });
      qc.invalidateQueries({ queryKey: ["agents-list", ws] });
      qc.invalidateQueries({ queryKey: ["table", "/agents/agents/"] });
    },
    onError: (e: Error) => setBuildError(e.message),
  });

  const trace = useChannel(selected ? `/agents/runs/${selected}/` : null);

  // Track how long we've been waiting on an empty trace, so we can show
  // an actionable message (instead of an indefinite "Waiting for events…")
  // when no Celery worker has picked the run up. The 5s threshold matches
  // the backend's PENDING_STUCK_SECONDS in apps/agents/dispatch.py.
  const [stalledSeconds, setStalledSeconds] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [retryMsg, setRetryMsg] = useState<string | null>(null);
  useEffect(() => {
    setStalledSeconds(0);
    setRetryMsg(null);
    if (!selected) return;
    if (trace.events.length > 0) return;
    const t0 = Date.now();
    const id = window.setInterval(() => {
      setStalledSeconds(Math.floor((Date.now() - t0) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [selected, trace.events.length]);

  async function retryDispatch() {
    if (!selected || retrying) return;
    setRetrying(true);
    setRetryMsg(null);
    try {
      // Hitting the run detail endpoint triggers the backend's
      // promote_if_stuck() recovery path — no new endpoint required.
      await rawFetch(`/agents/runs/${selected}/`);
      setRetryMsg("Dispatched. Trace events should appear shortly.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setRetryMsg(`Retry failed: ${msg}`);
    } finally {
      setRetrying(false);
    }
  }

  // Default the chat picker to the first enabled agent once definitions load.
  const definitions = defs.data?.results ?? [];
  const _firstId = definitions.find((d) => d.enabled)?.id ?? definitions[0]?.id ?? "";
  useMemo(() => { if (!chatAgentId && _firstId) setChatAgentId(_firstId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [_firstId]);
  useEffect(() => {
    if (!focusedAgentId) return;
    if (!definitions.some((definition) => definition.id === focusedAgentId)) return;
    setChatAgentId(focusedAgentId);
  }, [definitions, focusedAgentId]);

  const focusedAgent = definitions.find((definition) => definition.id === focusedAgentId) ?? null;

  // Phase 13.4 / G4 — provider-credential posture for the currently selected
  // chat agent, rendered as a chip so the operator knows whether a live key is
  // wired (real LLM), a stub is in use (deterministic canned output), or a key
  // is missing entirely (run will pre-flight-fail).
  const credStatus = useQuery<CredentialStatus>({
    queryKey: ["agent-credential-status", chatAgentId, ws],
    queryFn: () => rawFetch<CredentialStatus>(`/agents/agents/${chatAgentId}/credential-status/`),
    enabled: Boolean(chatAgentId),
    staleTime: 30000,
    retry: false,
  });
  const cred = credStatus.data;

  return (
    <>
      <PageHeader
        title="Agents"
        subtitle="LangGraph workflows grounded in your ontology + retrieval profile."
        help={
          <>
            <p>An agent is a reusable <strong>workflow</strong> — a system prompt, a model (the agent's pin, your workspace BYOK default, else the safe <code>stub</code>), tools (HTTP, MCP, retrieval), an <Link to="/ontology" className="text-accent hover:underline">ontology version</Link> and a <Link to="/retrieval" className="text-accent hover:underline">retrieval profile</Link>. Every call is logged as an <code>AgentRun</code> with full traces + governance audit.</p>
            <p><strong>default</strong> runs a single tool-calling loop; <strong>supervisor</strong> fans out to sub-agents when a question spans domains. Chat attaches short text files; upload larger corpora to <Link to="/catalog" className="text-accent hover:underline">Catalog</Link> so the agent can retrieve them.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "agents", label: "How agents work" }}
      />

      {/* -------------------------------------- G17: worker-health banner */}
      {workerDown && wh && (
        <div className="card mb-4 border-warn/40 bg-warn/5 text-xs text-warn">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle size={14} />
            {wh.dispatch === "inline"
              ? "No Celery worker online — runs execute synchronously (inline) and may block."
              : "Background worker degraded — agent runs may queue without executing."}
          </div>
          <ul className="mt-1 space-y-0.5 pl-6 text-warn/80">
            {!wh.broker.reachable && (
              <li>Broker unreachable{wh.broker.error ? `: ${wh.broker.error}` : "."}</li>
            )}
            {!wh.workers.online && (
              <li>
                No workers responding to ping
                {wh.workers.error ? `: ${wh.workers.error}` : "."}
              </li>
            )}
            {wh.workers.online && wh.workers.count > 0 && (
              <li>{wh.workers.count} worker(s) online: {wh.workers.names.join(", ")}</li>
            )}
          </ul>
        </div>
      )}

      {/* ------------------------------------------------ Build your agent */}
      <section className="card mb-4 border-accent/30">
        <div className="mb-3 flex items-center gap-2">
          <Wand2 size={16} className="text-accent" />
          <h2 className="text-sm font-semibold">Build your agent</h2>
          <span className="rounded-full border border-accent/40 px-2 py-0.5 text-[10px] text-accent">
            3 free builds
          </span>
        </div>
        <p className="mb-3 text-xs text-muted">
          Describe what you need in plain language. We'll propose the smallest
          system that covers it — a single agent, or a supervisor coordinating
          sub-agents. Nothing is created until you review and confirm. Free-tier
          builds are directional and ship as-is, without expert review.
        </p>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_2fr]">
          <label className="flex flex-col gap-0.5 text-[11px]">
            <span className="uppercase tracking-wide text-muted">Name (optional)</span>
            <input
              className="input"
              value={buildName}
              onChange={(e) => setBuildName(e.target.value)}
              placeholder="e.g. Claims helper"
            />
          </label>
          <label className="flex flex-col gap-0.5 text-[11px]">
            <span className="uppercase tracking-wide text-muted">What should it do?</span>
            <textarea
              className="input min-h-[64px]"
              value={buildNeed}
              onChange={(e) => setBuildNeed(e.target.value)}
              placeholder="e.g. Retrieve evidence from our policy docs, draft a grounded reply, then open a ticket and notify the owner."
            />
          </label>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={buildScope.isPending || !buildNeed.trim()}
            onClick={() => buildScope.mutate()}
          >
            {buildScope.isPending ? "Designing…" : "Propose a design"}
          </button>
          {buildSession?.blueprint && buildSession.status !== "generated" && (
            <span className="text-[11px] text-muted">
              Review the proposal below, then confirm to create the agents.
            </span>
          )}
        </div>

        {buildError && <div className="mt-3"><ErrorBox error={new Error(buildError)} /></div>}

        {buildQuota && (
          <div className="mt-3 rounded-md border border-warn/40 bg-warn/10 p-3 text-xs">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-warn">
              <AlertTriangle size={13} /> Free build limit reached
            </div>
            <p className="text-muted">
              You've used {buildQuota.current} of {buildQuota.limit} free agent
              builds. {buildQuota.hint || "Upgrade to build more agents, or edit one of your existing agents."}
            </p>
            <Link to="/settings" className="mt-1 inline-block text-accent underline">
              Upgrade plan →
            </Link>
          </div>
        )}

        {buildSession?.blueprint && (
          <BlueprintProposal
            blueprint={buildSession.blueprint}
            generated={buildSession.status === "generated"}
            generating={buildGenerate.isPending}
            onGenerate={() => buildGenerate.mutate()}
          />
        )}
      </section>
      {!probe.isLoading && (probe.data?.count ?? 0) === 0 && (
        <div className="card mb-4 text-sm">
          No agents in this workspace yet. Install an industry{" "}
          <Link to="/marketplace" className="text-accent underline">Pack</Link>{" "}
          to get a ready-made set (claims-triage, underwriting-assistant, …).
          Agents read from the documents you push in via{" "}
          <Link to="/catalog" className="text-accent underline">Catalog</Link>.
        </div>
      )}
      {probe.isError && <ErrorBox error={probe.error} />}

      {/* ------------------------------------------------ Chat (primary surface) */}
      <section className="card mb-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Send size={16} className="text-accent" />
            <h2 className="text-sm font-semibold">Chat with an agent</h2>
            {threadId && (
              <span className="rounded-full border border-accent/40 px-2 py-0.5 text-[10px] text-accent">
                thread {shortId(threadId)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted">
            {threadId && (
              <button
                type="button"
                className="hover:text-accent"
                onClick={() => { setThreadId(""); setSelected(null); }}
                title="Start a new conversation thread (clears memory)"
              >
                ↺ new thread
              </button>
            )}
            <span>Live trace · WS {trace.status}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
          {/* ---- Composer ---- */}
          <div>
            <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_1fr]">
              <label className="flex flex-col gap-0.5 text-[11px]">
                <span className="uppercase tracking-wide text-muted">Agent</span>
                <select
                  className="input text-xs"
                  value={chatAgentId}
                  onChange={(e) => setChatAgentId(e.target.value)}
                  disabled={defs.isLoading || definitions.length === 0}
                >
                  {definitions.length === 0
                    ? <option value="">No agents yet — create one below</option>
                    : definitions.map((d) => (
                        <option key={d.id} value={d.id}>{d.name} ({d.model})</option>
                      ))}
                </select>
                {/* G4: provider-credential posture for the selected agent */}
                {cred && (
                  <span
                    className={
                      "mt-1 inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] " +
                      (cred.mode === "live"
                        ? "border-good/50 text-good"
                        : cred.mode === "stub"
                          ? "border-accent/50 text-accent"
                          : "border-bad/50 text-bad")
                    }
                    title={cred.detail || `${cred.provider ?? "provider"} · ${cred.model ?? "model"}`}
                  >
                    {cred.mode === "live" ? (
                      <><CheckCircle2 size={10} /> live key</>
                    ) : cred.mode === "stub" ? (
                      <><Sparkles size={10} /> stub (no live LLM)</>
                    ) : (
                      <><AlertTriangle size={10} /> no key</>
                    )}
                  </span>
                )}
              </label>
              <label className="flex flex-col gap-0.5 text-[11px]">
                <span className="uppercase tracking-wide text-muted">Runtime</span>
                <select className="input text-xs" value={runtimeMode}
                        onChange={(e) => setRuntimeMode(e.target.value as "default" | "supervisor")}>
                  <option value="default">default (single)</option>
                  <option value="supervisor">supervisor (LangGraph)</option>
                </select>
              </label>
              <label className="flex flex-col gap-0.5 text-[11px]">
                <span className="uppercase tracking-wide text-muted">Max iters</span>
                <input className="input text-xs" type="number" min={1} max={20}
                       value={maxIterations}
                       onChange={(e) => setMaxIterations(Math.max(1, Math.min(20, Number(e.target.value) || 6)))} />
              </label>
            </div>

            <textarea
              className="input min-h-[160px] text-sm"
              placeholder="Type your message… the agent will use its ontology + retrieval profile to answer."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            {files.length > 0 && (
              <ul className="mt-2 space-y-1 text-[11px]">
                {files.map((f, idx) => (
                  <li key={idx} className="flex items-center justify-between rounded border border-border bg-bg/40 px-2 py-1">
                    <span className="truncate">
                      <Paperclip size={10} className="mr-1 inline -mt-0.5" />
                      {f.name} <span className="text-muted">({Math.ceil(f.size / 1024)} KB)</span>
                    </span>
                    <button type="button" onClick={() => setFiles((fs) => fs.filter((_, i) => i !== idx))}
                            className="text-muted hover:text-bad" aria-label={`Remove ${f.name}`}>
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".txt,.md,.csv,.json,.yaml,.yml,.tsv,.log,text/*"
                className="hidden"
                onChange={(e) => {
                  const list = Array.from(e.target.files ?? []).slice(0, 4);
                  setFiles((prev) => [...prev, ...list].slice(0, 4));
                }}
              />
              <button
                type="button"
                className="btn text-xs"
                onClick={() => fileInputRef.current?.click()}
                title="Attach up to 4 text files, 64 KB each. Larger / binary → Catalog."
              >
                <Paperclip size={12} className="mr-1 -mt-0.5 inline" />
                Attach files
              </button>
              <span className="text-[10px] text-muted">
                text only, ≤4 files × 64 KB. For PDFs / images / large corpora use{" "}
                <Link to="/catalog" className="text-accent underline">Catalog</Link>.
              </span>
              <button
                type="button"
                className="btn-primary ml-auto text-xs"
                disabled={chat.isPending || !chatAgentId}
                onClick={() => chat.mutate()}
              >
                <Send size={12} className="mr-1 -mt-0.5 inline" />
                {chat.isPending ? "Sending…" : "Send"}
              </button>
            </div>

            {preflight && (
              <div className="mt-2 rounded border border-warn/40 bg-warn/10 p-3 text-[12px] text-warn">
                <div className="mb-1 font-semibold">
                  {preflight.error || "Agent is not ready to run."}
                </div>
                {preflight.hint && (
                  <div className="mb-2 leading-snug">{preflight.hint}</div>
                )}
                {preflight.link && (
                  <a
                    href={preflight.link}
                    className="inline-block rounded border border-warn/40 bg-warn/15 px-2 py-1 text-[11px] font-semibold text-warn hover:bg-warn/25"
                  >
                    {preflight.code === "provider_credentials_missing"
                      ? "Add API key →"
                      : "Configure model →"}
                  </a>
                )}
                <div className="mt-1 text-[10px] opacity-70">code: {preflight.code}</div>
              </div>
            )}

            {chatError && (
              <div className={`mt-2 rounded border p-2 text-[11px] ${
                chatError.toLowerCase().includes("migration") || chatError.toLowerCase().includes("not available")
                  ? "border-warn/40 bg-warn/5 text-warn"
                  : "border-bad/40 bg-bad/10 text-bad"
              }`}>
                {chatError}
              </div>
            )}
          </div>

          {/* ---- Live trace ---- */}
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wide text-muted">
              Run trace {selected ? <code className="font-mono">{shortId(selected)}</code> : ""}
            </div>
            <div className="max-h-[360px] min-h-[200px] space-y-1 overflow-auto rounded border border-border bg-bg/30 p-2 text-xs">
              {selected ? (trace.events.length === 0
                ? (
                    <div className="space-y-2 text-muted">
                      <div>
                        Waiting for events… <span className="text-[10px] opacity-70">({stalledSeconds}s)</span>
                      </div>
                      {stalledSeconds >= 5 && (
                        <div className="rounded border border-warn/40 bg-warn/10 p-2 text-[11px] text-warn">
                          <div className="mb-1 font-semibold">No worker has picked up this run yet.</div>
                          <div className="mb-2 leading-snug">
                            Most common causes: the Celery <code className="font-mono">agents</code> queue worker is not running,
                            the Redis broker is unreachable, or the worker rejected the task as <em>unregistered</em>
                            (check that <code className="font-mono">apps.agents.runtime</code> is imported on boot).
                            The backend will attempt an in-process fallback automatically; click <em>Retry dispatch</em> to nudge it now.
                          </div>
                          <button
                            type="button"
                            onClick={retryDispatch}
                            disabled={retrying}
                            className="rounded border border-warn/40 bg-warn/15 px-2 py-0.5 text-[11px] font-medium text-warn hover:bg-warn/25 disabled:opacity-50"
                          >
                            {retrying ? "Retrying…" : "Retry dispatch"}
                          </button>
                          {retryMsg && <div className="mt-1 text-[10px] opacity-90">{retryMsg}</div>}
                        </div>
                      )}
                    </div>
                  )
                : trace.events.map((e, i) => (
                    <div key={i} className="border-b border-border/40 py-1 last:border-b-0">
                      <div><span className="text-accent">[{e.type}]</span>{" "}
                        <span className="text-muted">{e.ts}</span></div>
                      <code className="block whitespace-pre-wrap break-words font-mono text-[10px] text-muted">
                        {JSON.stringify(e.payload, null, 2)}
                      </code>
                    </div>
                  ))
              ) : <div className="text-muted">Send a message or click a run id in the table below to subscribe.</div>}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ Connect from your app */}
      <section className="card mb-4 border-accent/30">
        <button
          type="button"
          className="flex w-full items-center justify-between text-left"
          onClick={() => setShowConnect((v) => !v)}
        >
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Plug size={15} className="text-accent" />
            Connect from your app (API)
          </div>
          <span className="text-xs text-muted">{showConnect ? "hide" : "show"}</span>
        </button>
        {showConnect && (
          <div className="mt-3 text-xs text-muted">
            <p className="mb-2">
              Don't trap this behind the in-app chat. Call your agent from your own
              website, backend, or a customer-facing chatbot with a workspace{" "}
              <Link to="/settings" className="text-accent underline">API key</Link>.
              The snippet below targets{" "}
              {chatAgentId
                ? <>the agent you selected above (<code className="font-mono">{shortId(chatAgentId)}</code>)</>
                : <>your chosen agent</>}.
            </p>
            {(() => {
              const agentId = chatAgentId || "<agent-id>";
              const snippet =
`# synchronous: one call, grounded answer in the response
curl -X POST https://api.ontoligent.io/api/v1/agents/agents/${agentId}/ask/ \\
  -H "Authorization: ApiKey <your-key>" \\
  -H "X-Tenant-Schema: ${tenantSlug}" \\
  -H "Content-Type: application/json" \\
  -d '{"message":"Third-party fire-damage claim, photos attached. Next steps?","thread_id":"case-8842"}'
# -> 200 { "status":"succeeded", "output": { "content":"…", "citations":[…] } }

# long / supervisor jobs: submit (async) then poll the run
# POST …/agents/agents/${agentId}/chat/  ->  GET …/agents/runs/<run-id>/`;
              return (
                <div className="relative">
                  <button
                    type="button"
                    className="btn-ghost absolute right-1 top-1 text-[11px]"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(snippet);
                        setConnectCopied(true);
                        setTimeout(() => setConnectCopied(false), 1500);
                      } catch { /* clipboard blocked */ }
                    }}
                  >
                    <Copy size={12} /> {connectCopied ? "Copied" : "Copy"}
                  </button>
                  <pre className="overflow-x-auto rounded bg-bg p-3 text-[10px] leading-snug text-fg">
                    {snippet}
                  </pre>
                </div>
              );
            })()}
            <p className="mt-2">
              Keep the key server-side, pass a stable <code>thread_id</code> for
              memory, and feed closed cases back via{" "}
              <code>POST /catalog/sources/quick_ingest/</code> so answers keep
              improving. Full guide:{" "}
              <Link to="/docs" className="text-accent underline">Docs &rarr; Embed in your app</Link>.
            </p>
          </div>
        )}
      </section>

      {/* ------------------------------------------------ Industry templates */}
      <section className="card mb-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-accent" />
            <h2 className="text-sm font-semibold">Industry templates</h2>
            <span className="rounded-full border border-accent/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-accent">
              one-click agent
            </span>
          </div>
          <select
            className="input w-auto text-xs"
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
            aria-label="Filter templates by industry"
          >
            <option value="">All industries</option>
            <option value="healthcare">Healthcare</option>
            <option value="finance">Finance</option>
            <option value="legal">Legal</option>
            <option value="retail">Retail</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="saas">SaaS</option>
            <option value="platform">Platform</option>
          </select>
        </div>
        <p className="mb-3 text-xs text-muted">
          Spin up a pre-configured agent grounded in your published{" "}
          <Link to="/ontology" className="text-accent underline">ontology</Link>{" "}
          + default retrieval profile. Each template ships with a domain
          system prompt and a curated tool set.
        </p>
        {templates.isLoading && <div className="text-xs text-muted">Loading templates…</div>}
        {templates.isError && <ErrorBox error={templates.error} />}
        {templates.data && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {templates.data.templates.map((t) => {
              const count = t.installed_count ?? 0;
              const installed = count > 0;
              return (
              <div key={t.id} className={`rounded border p-3 ${installed ? "border-good/40 bg-good/5" : "border-border bg-bg/40"}`}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-semibold">{t.display_name}</span>
                  <code className="font-mono text-[10px] text-muted">{t.industry}</code>
                </div>
                <p className="mb-2 text-xs text-muted">{t.description}</p>
                <div className="mb-2 flex items-center justify-between text-[10px] text-muted">
                  <span>default: <code className="font-mono">{t.default_model}</code></span>
                  {installed && (
                    <span className="rounded-full border border-good/50 px-2 py-0.5 text-good">
                      Installed{count > 1 ? ` ×${count}` : ""}
                    </span>
                  )}
                </div>
                <button
                  className={installed ? "btn w-full text-xs" : "btn-primary w-full text-xs"}
                  disabled={!ws || createFromTemplate.isPending}
                  onClick={() => createFromTemplate.mutate(t.id)}
                  title={installed
                    ? "An agent from this template already exists in this workspace. Click to create another variant."
                    : "Create a new agent from this template."}
                >
                  {createFromTemplate.isPending ? "Creating…" : (installed ? "Create another" : "Create agent")}
                </button>
              </div>
              );
            })}
          </div>
        )}
        {createFromTemplate.isError && <ErrorBox error={createFromTemplate.error} />}
        {createFromTemplate.isSuccess && (
          <div className="mt-2 text-xs text-good">
            Agent created from template — see Definitions table below.
          </div>
        )}
      </section>

      <h2 className="mb-2 text-sm font-medium text-muted">Definitions</h2>
      {focusedAgent && (
        <div className="card mb-2 border-good/30 bg-good/5 text-xs text-good">
          Focused agent: <strong>{focusedAgent.name}</strong>. It is currently {focusedAgent.enabled ? "enabled" : "disabled"}; review it below and activate only when ready.
        </div>
      )}
      {gateError && (
        <div className="card mb-2 border-warn/40 bg-warn/5 text-xs text-warn">
          <span className="mr-1">🚦</span>
          <strong>Eval gate blocked activation:</strong> {gateError}
        </div>
      )}
      <PaginatedTable<AgentDef>
        endpoint="/agents/agents/"
        baseParams={{ workspace: ws ?? undefined }}
        searchPlaceholder="Search by name, model, or system prompt…"
        rowClassName={(agent) => (
          agent.id === focusedAgentId
            ? "bg-good/10 ring-1 ring-inset ring-good/30"
            : undefined
        )}
        cols={[
          { header: "Name",  cell: (a) => (
              <span className="inline-flex items-center gap-1.5">
                {a.name}
                {a.has_structured_output && (
                  <span
                    className="inline-flex h-4 w-4 items-center justify-center rounded border border-accent/50 text-[9px] font-bold text-accent"
                    title="Structured output — this agent parses runs into a declared JSON-Schema object."
                  >
                    P
                  </span>
                )}
              </span>
            ),
          },

          { header: "Model", cell: (a) => <code className="font-mono text-xs">{a.model}</code> },
          { header: "On",    cell: (a) => <StatusPill status={a.enabled ? "active" : "draft"} /> },
          { header: "",      width: "1%", cell: (a) => (
              <div className="flex items-center gap-1">
                {a.enabled ? (
                  <button className="btn text-xs" disabled={deactivate.isPending}
                    onClick={() => deactivate.mutate(a.id)}
                    title="Deactivate this agent.">Deactivate</button>
                ) : (
                  <button className="btn text-xs" disabled={activate.isPending}
                    onClick={() => activate.mutate(a.id)}
                    title="Activate — runs an eval-regression gate first; blocked if it would degrade the golden set.">
                    Activate
                  </button>
                )}
                <button className="btn-primary text-xs" disabled={trigger.isPending}
                  onClick={() => trigger.mutate(a.id)}>Trigger</button>
              </div>
            ),
          },
        ]}
        empty="No agents yet."
      />

      <div className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-muted">Runs</h2>
        <PaginatedTable<AgentRun>
          endpoint="/agents/runs/"
          baseParams={{ workspace: ws ?? undefined }}
          statusOptions={[
            { label: "Pending",   value: "pending"   },
            { label: "Running",   value: "running"   },
            { label: "Succeeded", value: "succeeded" },
            { label: "Failed",    value: "failed"    },
          ]}
          searchPlaceholder="Search by agent name, triggered_by, or status…"
          refetchInterval={5000}
          cols={[
            { header: "ID", cell: (r) => (
                <button className="font-mono text-xs underline-offset-2 hover:underline"
                        onClick={() => setSelected(r.id)}>
                  {shortId(r.id)}
                </button>
              ),
            },
            { header: "Status",   cell: (r) => <StatusPill status={r.status} /> },
            { header: "Created",  cell: (r) => fmtDate(r.created_at) },
            { header: "Finished", cell: (r) => fmtDate(r.finished_at) },
            { header: "", width: "1%", cell: (r) => (
                <Link to="/dossier" search={{ run: r.id }}
                      className="text-xs underline-offset-2 hover:underline"
                      title="Prove why this decision happened">
                  Dossier
                </Link>
              ),
            },
          ]}
        />
      </div>
    </>
  );
}

function confidenceTone(pct: number): { cls: string; label: string } {
  if (pct >= 67) return { cls: "border-good/50 text-good", label: "good match" };
  if (pct >= 50) return { cls: "border-accent/50 text-accent", label: "directional" };
  return { cls: "border-warn/50 text-warn", label: "low confidence" };
}

function BlueprintProposal({
  blueprint, generated, generating, onGenerate,
}: {
  blueprint: AgentBlueprint;
  generated: boolean;
  generating: boolean;
  onGenerate: () => void;
}) {
  const pct = Math.round((blueprint.confidence ?? 0) * 100);
  const tone = confidenceTone(pct);
  const specs: SubAgentSpec[] = blueprint.supervisor
    ? [blueprint.supervisor, ...blueprint.agents]
    : blueprint.agents;

  return (
    <div className="mt-4 rounded-md border border-border bg-bg/40 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted">
          {blueprint.topology === "supervisor" ? "Supervisor + sub-agents" : "Single agent"}
        </span>
        <span className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${tone.cls}`}>
          <AlertTriangle size={11} /> ~{pct}% match · {tone.label}
        </span>
      </div>

      <p className="mb-2 text-xs text-fg">{blueprint.summary}</p>
      <p className="mb-2 text-[11px] italic text-muted">{blueprint.rationale}</p>

      {blueprint.caveat && (
        <div className="mb-3 flex items-start gap-1.5 rounded-md border border-warn/40 bg-warn/10 p-2 text-[11px] text-muted">
          <AlertTriangle size={12} className="mt-0.5 shrink-0 text-warn" />
          <span>{blueprint.caveat}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {specs.map((s, i) => (
          <div key={i} className="rounded border border-border bg-bg/40 p-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold">{s.name}</span>
              <code className="font-mono text-[10px] text-muted">{s.role}</code>
            </div>
            <p className="mb-1 line-clamp-3 text-[11px] text-muted">{s.system_prompt}</p>
            <div className="flex flex-wrap gap-1">
              {s.tools.length === 0 && (
                <span className="text-[10px] text-muted">no tools</span>
              )}
              {s.tools.map((t) => (
                <span key={t.key} className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted" title={t.why}>
                  {t.key}
                </span>
              ))}
              {s.needs_retrieval && (
                <span className="rounded border border-accent/40 px-1.5 py-0.5 text-[10px] text-accent">
                  retrieval
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        {generated ? (
          <span className="flex items-center gap-1.5 text-xs text-good">
            <CheckCircle2 size={14} /> Agents created (disabled) — review and activate them in the Definitions table below.
          </span>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={generating}
              onClick={onGenerate}
            >
              {generating ? "Creating…" : "Looks good — create these agents"}
            </button>
            <span className="text-[11px] text-muted">
              Created disabled; you activate them after a preflight check.
            </span>
          </>
        )}
      </div>
    </div>
  );
}

